import React from 'react';
import { Skeleton, Card, Table } from 'antd';

/**
 * Loading skeleton types
 */
export type SkeletonType = 'table' | 'form' | 'detail' | 'card' | 'list' | 'custom';

/**
 * Loading skeleton props
 */
export interface LoadingSkeletonProps {
  type?: SkeletonType;
  loading?: boolean;
  children?: React.ReactNode;
  rows?: number;
  columns?: number;
  avatar?: boolean;
  title?: boolean;
  paragraph?: boolean | { rows?: number; width?: string | string[] };
  active?: boolean;
  className?: string;
}

/**
 * Table skeleton component
 */
const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({ 
  rows = 5, 
  columns = 6 
}) => {
  const tableColumns = Array.from({ length: columns }, (_, index) => ({
    title: <Skeleton.Input style={{ width: 100 }} active />,
    dataIndex: `col${index}`,
    key: `col${index}`,
    render: () => <Skeleton.Input style={{ width: '100%' }} active />,
  }));

  const tableData = Array.from({ length: rows }, (_, index) => ({
    key: index,
    ...Object.fromEntries(
      Array.from({ length: columns }, (_, colIndex) => [`col${colIndex}`, `data${index}-${colIndex}`])
    ),
  }));

  return (
    <div className="table-skeleton">
      <div style={{ marginBottom: 16 }}>
        <Skeleton.Input style={{ width: 200, marginRight: 16 }} active />
        <Skeleton.Input style={{ width: 120 }} active />
      </div>
      <Table
        columns={tableColumns}
        dataSource={tableData}
        pagination={false}
        size="middle"
        bordered
        scroll={{ y: 500 }}
      />
      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Skeleton.Input style={{ width: 300 }} active />
      </div>
    </div>
  );
};

/**
 * Form skeleton component
 */
const FormSkeleton: React.FC<{ rows?: number }> = ({ rows = 6 }) => {
  return (
    <div className="form-skeleton">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} style={{ marginBottom: 24 }}>
          <Skeleton.Input 
            style={{ width: 120, marginBottom: 8 }} 
            active 
            size="small" 
          />
          <Skeleton.Input 
            style={{ width: '100%', height: 32 }} 
            active 
          />
        </div>
      ))}
      <div style={{ marginTop: 32, textAlign: 'right' }}>
        <Skeleton.Button style={{ marginRight: 8 }} active />
        <Skeleton.Button active />
      </div>
    </div>
  );
};

/**
 * Detail skeleton component
 */
const DetailSkeleton: React.FC<{ rows?: number }> = ({ rows = 8 }) => {
  return (
    <div className="detail-skeleton">
      <div style={{ marginBottom: 24 }}>
        <Skeleton.Input style={{ width: 300, height: 32 }} active />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} style={{ marginBottom: 16 }}>
            <Skeleton.Input 
              style={{ width: 100, marginBottom: 8 }} 
              active 
              size="small" 
            />
            <Skeleton.Input 
              style={{ width: '100%' }} 
              active 
            />
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Card skeleton component
 */
const CardSkeleton: React.FC<{ rows?: number }> = ({ rows = 3 }) => {
  return (
    <Card>
      <Skeleton
        avatar
        paragraph={{ rows }}
        active
      />
    </Card>
  );
};

/**
 * List skeleton component
 */
const ListSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => {
  return (
    <div className="list-skeleton">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} style={{ 
          display: 'flex', 
          alignItems: 'center', 
          padding: '12px 0',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Skeleton.Avatar style={{ marginRight: 16 }} active />
          <div style={{ flex: 1 }}>
            <Skeleton.Input style={{ width: '60%', marginBottom: 8 }} active />
            <Skeleton.Input style={{ width: '40%' }} active size="small" />
          </div>
          <Skeleton.Button active />
        </div>
      ))}
    </div>
  );
};

/**
 * Main loading skeleton component
 */
const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  type = 'custom',
  loading = true,
  children,
  rows = 5,
  columns = 6,
  avatar = false,
  title = true,
  paragraph = true,
  active = true,
  className = '',
}) => {
  if (!loading && children) {
    return <>{children}</>;
  }

  if (!loading) {
    return null;
  }

  const renderSkeleton = () => {
    switch (type) {
      case 'table':
        return <TableSkeleton rows={rows} columns={columns} />;
      
      case 'form':
        return <FormSkeleton rows={rows} />;
      
      case 'detail':
        return <DetailSkeleton rows={rows} />;
      
      case 'card':
        return <CardSkeleton rows={rows} />;
      
      case 'list':
        return <ListSkeleton rows={rows} />;
      
      case 'custom':
      default:
        return (
          <Skeleton
            avatar={avatar}
            title={title}
            paragraph={paragraph}
            active={active}
          />
        );
    }
  };

  return (
    <div className={`loading-skeleton ${className}`}>
      {renderSkeleton()}
    </div>
  );
};

/**
 * Container-specific loading skeletons
 */
export const ContainerSkeletons = {
  /**
   * Container list table skeleton
   */
  Table: () => (
    <LoadingSkeleton 
      type="table" 
      rows={10} 
      columns={8}
    />
  ),

  /**
   * Container form skeleton
   */
  Form: () => (
    <LoadingSkeleton 
      type="form" 
      rows={6}
    />
  ),

  /**
   * Container detail skeleton
   */
  Detail: () => (
    <LoadingSkeleton 
      type="detail" 
      rows={12}
    />
  ),

  /**
   * Container filter panel skeleton
   */
  FilterPanel: () => (
    <div style={{ 
      display: 'flex', 
      gap: 16, 
      padding: 16, 
      background: '#fafafa',
      borderRadius: 6,
      marginBottom: 16
    }}>
      <Skeleton.Input style={{ width: 200 }} active />
      <Skeleton.Input style={{ width: 150 }} active />
      <Skeleton.Button active />
      <Skeleton.Button active />
    </div>
  ),

  /**
   * Container card list skeleton
   */
  CardList: () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
      {Array.from({ length: 6 }, (_, index) => (
        <LoadingSkeleton key={index} type="card" rows={3} />
      ))}
    </div>
  ),

  /**
   * Container batch actions skeleton
   */
  BatchActions: () => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      marginBottom: 16
    }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <Skeleton.Button active />
        <Skeleton.Button active />
      </div>
      <Skeleton.Input style={{ width: 200 }} active />
    </div>
  ),
};

/**
 * React hook for loading skeleton
 */
export function useLoadingSkeleton(loading: boolean) {
  return {
    loading,
    Skeleton: LoadingSkeleton,
    ContainerSkeletons,
    withSkeleton: <T extends {}>(Component: React.ComponentType<T>, skeletonType: SkeletonType = 'custom') => 
      (props: T & { loading?: boolean }) => (
        <LoadingSkeleton type={skeletonType} loading={props.loading}>
          <Component {...props} />
        </LoadingSkeleton>
      ),
  };
}

export default LoadingSkeleton;